import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FlaskConicalIcon, HeartHandshakeIcon, LayersIcon, UsersIcon, TimerIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PAGE_SAMPLES } from './assets';
// All copy of the PRZYRODA GOTOWA™ landing (Poland, PLN). Source of truth: przyroda-gotowa/PRODUCTO.md.
// Compliance: no «zatwierdzone przez MEN», no «certyfikowane», no testimonials or invented numbers.
export const BRAND = 'PRZYRODA GOTOWA™';
export const heroGuarantees = ['Natychmiastowy dostęp do PDF', 'Płatność jednorazowa', 'Bez subskrypcji', '7 dni gwarancji'];
export const stats = [{
  value: '240',
  label: 'Stron w 6 tomach'
}, {
  value: '6',
  label: 'Tomów PDF do druku'
}, {
  value: '12',
  label: 'Lekcji minuta po minucie (45 min)'
}, {
  value: '25+',
  label: 'Doświadczeń krok po kroku'
}, {
  value: '5',
  label: 'Bonusów w cenie'
}];
export const differenceStats = [{
  value: 'Klasy 1–8',
  label: 'Edukacja przyrodnicza, przyroda, biologia, chemia, fizyka i geografia'
}, {
  value: '6 tomów · 240 stron',
  label: 'Plan roczny, gotowe lekcje, doświadczenia, karty pracy i ocenianie'
}, {
  value: 'Karty pracy na 3 poziomach',
  label: '★ wsparcie · ★★ standard · ★★★ wyzwanie – do druku lub na tablicę'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Wieczory i weekendy na planowaniu',
  text: 'Kolejną niedzielę spędzasz nad konspektami na przyszły tydzień – a w poniedziałek i tak część lekcji improwizujesz.'
}, {
  title: 'Nowa przyroda w klasie 4 – a materiałów brak',
  text: 'Od 1 września 2026 r. przyroda wróciła do klasy 4 w nowej formule. Lekcje trzeba ułożyć od nowa, a sprawdzonych materiałów wciąż jest mało.'
}, {
  title: '28 uczniów, każdy na innym poziomie',
  text: 'Jedni kończą kartę pracy w 5 minut, inni potrzebują pomocy przy każdym zadaniu – a lekcja ma działać dla wszystkich.'
}, {
  title: 'Doświadczenia bez pracowni',
  text: 'Nowa przyroda stawia na doświadczenia i obserwacje, a Ty nie masz pracowni, sprzętu ani czasu, żeby wszystko przygotować i sprawdzić.'
}, {
  title: 'Sprawdziany i ocenianie po godzinach',
  text: 'Kartkówki, sprawdziany w dwóch grupach, klucze odpowiedzi i kryteria na oceny 1–6 – kolejne wieczory przy biurku.'
}, {
  title: 'Uczniowie z opinią PPP',
  text: 'Wymagania trzeba dostosować, a nie masz kiedy przygotować osobnych kart dla kilku uczniów w każdej klasie.'
}];
export const afterItems = ['Plan roczny dla kl. 1–3, 4–6 i 7–8 (ok. 36 tygodni)', '12 lekcji minuta po minucie – każda na 45 minut', 'MATERIAŁY i INSTRUKCJA DLA NAUCZYCIELA przy każdej lekcji', 'Karty pracy na 3 poziomach: wsparcie, standard, wyzwanie', 'Ponad 25 doświadczeń krok po kroku z zasadami BHP', 'Doświadczenia z prostych materiałów – także bez pracowni', 'Działy przyrody, biologii, chemii, fizyki i geografii', 'Kartkówki i sprawdziany A/B z kluczem odpowiedzi', 'Wymagania na oceny 1–6 i 100 gotowych komentarzy do ocen'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Wieczory znikają na planowaniu',
  text: 'Czy spędzasz wieczory i weekendy na przygotowywaniu lekcji przyrody na kolejny tydzień?'
}, {
  icon: TimerIcon,
  title: 'Dzwonek dzwoni, a lekcja nie jest gotowa',
  text: 'Wchodzisz do klasy i jeszcze zastanawiasz się, od czego zacząć i jaką kartę rozdać?'
}, {
  icon: UsersIcon,
  title: 'Uczniowie na bardzo różnych poziomach',
  text: 'Potrzebujesz zadań, które zadziałają i dla ucznia, który potrzebuje wsparcia, i dla tego, który się nudzi?'
}, {
  icon: FlaskConicalIcon,
  title: 'Doświadczenia wydają się zbyt trudne',
  text: 'Chcesz robić doświadczenia, ale bez pracowni nie wiesz, co jest bezpieczne i co naprawdę się uda?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Ocenianie zabiera mnóstwo czasu',
  text: 'Przydałyby Ci się gotowe kartkówki, sprawdziany A/B z kluczem i jasne kryteria na oceny 1–6?'
}, {
  icon: HeartHandshakeIcon,
  title: 'Dostosowania dla uczniów z opinią',
  text: 'Szukasz prostego sposobu, żeby uczeń z opinią PPP pracował nad tym samym tematem co cała klasa?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tom 01',
  level: 'Kl. 1–8',
  title: 'Plan roczny + 12 lekcji minuta po minucie',
  tagline: 'Wiesz dokładnie, co robisz na każdej lekcji – przez cały rok szkolny.',
  intro: 'Pierwszy tom daje Ci gotowy plan roczny dla wszystkich klas i dwanaście lekcji, które możesz poprowadzić od razu.',
  items: ['Mapa działów dla kl. 1–3, 4–6 i 7–8', 'Plan roczny na ok. 36 tygodni', 'Plan 12 tygodni i tabela progresji', 'BHP w pracowni i w klasie', '12 lekcji minuta po minucie (45 min)', 'MATERIAŁY + INSTRUKCJA DLA NAUCZYCIELA', 'Karty ucznia na 3 poziomach', '8 doświadczeń z zasadami bezpieczeństwa', 'Sprawdziany kl. 4–6 i 7–8 z kluczem', 'Kryteria oceniania'],
  outro: 'Gotowe lekcje, z którymi wchodzisz do klasy z jasnym planem.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 02',
  level: 'Kl. 1–6',
  title: 'Przyroda kl. 4–6 (+ edukacja przyrodnicza kl. 1–3)',
  tagline: 'Nowa przyroda w kl. 4–6 – bez układania wszystkiego od zera.',
  intro: 'Drugi tom jest ułożony pod nową przyrodę w kl. 4–6 i zawiera też materiały do edukacji przyrodniczej w kl. 1–3.',
  items: ['Obserwacja i pomiar', 'Mapa i orientacja w terenie', 'Pogoda i klimat', 'Organizmy i środowisko', 'Ciało człowieka i zdrowie', 'Substancje i ich właściwości', 'Krajobrazy Polski', 'Karty pracy na 3 poziomach', '5 doświadczeń i klucz odpowiedzi'],
  outro: 'Lekcje, na których uczniowie obserwują, mierzą i sami dochodzą do wniosków.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 03',
  level: 'Kl. 7–8',
  title: 'Biologia kl. 7–8',
  tagline: 'Od komórki do ekosystemu – jasno i obrazowo.',
  intro: 'Trzeci tom prowadzi uczniów przez najważniejsze działy biologii w klasach 7–8.',
  items: ['Komórka', 'Układ pokarmowy, krążenia, oddechowy i nerwowy', 'Genetyka: DNA i krzyżówki', 'Ewolucja', 'Ekologia: łańcuchy i sieci pokarmowe', 'Obieg materii', 'Karty pracy na 3 poziomach', '5 doświadczeń i klucz odpowiedzi'],
  outro: 'Biologia, którą uczniowie rozumieją, a nie tylko zapamiętują.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 04',
  level: 'Kl. 7–8',
  title: 'Chemia i fizyka kl. 7–8',
  tagline: 'Doświadczenia i obliczenia krok po kroku.',
  intro: 'Czwarty tom łączy chemię i fizykę: od budowy atomu po prąd elektryczny – z zadaniami rachunkowymi i rozwiązaniami.',
  items: ['Substancje i mieszaniny', 'Budowa atomu i układ okresowy', 'Reakcje chemiczne', 'Kwasy i zasady (wskaźnik z czerwonej kapusty)', 'Ruch i siły, energia', 'Prąd elektryczny i magnetyzm', 'Światło i dźwięk', 'Zadania z obliczeniami i rozwiązaniami', '5 doświadczeń i klucz odpowiedzi'],
  outro: 'Chemia i fizyka bez stresu – dla Ciebie i dla uczniów.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 05',
  level: 'Kl. 7–8',
  title: 'Geografia kl. 7–8 + zajęcia terenowe',
  tagline: 'Mapa, klimat i zajęcia w terenie – gotowe do poprowadzenia.',
  intro: 'Piąty tom obejmuje geografię Polski i Europy oraz gotowe karty obserwacji do zajęć terenowych.',
  items: ['Mapa Polski i Europy', 'Skala i współrzędne geograficzne', 'Klimat i pogoda', 'Ludność i miasta', 'Środowisko i ochrona przyrody', 'Karty obserwacji terenowej', '4 doświadczenia i obserwacje', 'Klucz odpowiedzi'],
  outro: 'Geografia, która wychodzi poza podręcznik – i poza klasę.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 06',
  level: 'Kl. 4–8',
  title: 'Ocenianie',
  tagline: 'Sprawiedliwe, jasne i szybkie ocenianie.',
  intro: 'Szósty tom zbiera wszystko, czego potrzebujesz do oceniania z przyrody, biologii, chemii, fizyki i geografii.',
  items: ['Wymagania na oceny 1–6', 'Ocenianie kształtujące', 'Kartkówki', 'Sprawdziany A/B z kluczem', 'Przyroda, biologia, chemia, fizyka i geografia', 'Samoocena ucznia', '100 gotowych komentarzy do ocen'],
  outro: 'Ocenianie bez wieczorów nad stosem kartek.',
  note: 'Materiał opracowany niezależnie – nie jest publikacją MEN.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 tomów PDF z przyrody',
  text: 'Kompletny system, który pomaga zaplanować i poprowadzić lekcje przyrody, biologii, chemii, fizyki i geografii przez cały rok szkolny. Każdy tom ma 40 stron: gotowe lekcje, doświadczenia, karty pracy na 3 poziomach i klucze odpowiedzi.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Gotowe lekcje minuta po minucie',
  text: 'Koniec z pustą kartką. Cel, pytanie kluczowe, MATERIAŁY, INSTRUKCJA DLA NAUCZYCIELA i przebieg całej 45-minutowej lekcji.'
}, {
  icon: FlaskConicalIcon,
  title: 'Doświadczenia krok po kroku',
  text: 'Ponad 25 doświadczeń z listą materiałów, krokami i ramką BEZPIECZEŃSTWO – wiele z nich z prostych materiałów, bez pracowni.'
}, {
  icon: LayersIcon,
  title: '3 poziomy',
  text: 'Ta sama karta pracy w trzech wersjach: ★ wsparcie, ★★ standard, ★★★ wyzwanie. Jeden temat dla całej klasy.'
}, {
  icon: FileTextIcon,
  title: 'Karty pracy do druku',
  text: 'Drukujesz tylko to, czego potrzebujesz – albo wyświetlasz kartę na tablicy interaktywnej.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Sprawdziany z kluczem',
  text: 'Kartkówki, sprawdziany A/B z kluczem odpowiedzi, wymagania na oceny 1–6 i 100 gotowych komentarzy do ocen.'
}, {
  icon: CalendarCheckIcon,
  title: 'Plan roczny',
  text: 'Ok. 36 tygodni rozpisanych dla kl. 1–3, 4–6 i 7–8 – ułożone pod nową przyrodę w kl. 4–6.'
}];

// Labels of the 5 sample pages of Tom 1 «Zobacz w środku». Same order as PAGE_SAMPLES in data/assets.ts.
export const pageLabels: Array<{
  label: string;
  title: string;
  text: string;
}> = [{
  label: 'Tom 1 · Okładka',
  title: 'Plan roczny + 12 lekcji minuta po minucie',
  text: 'Tom 1 obejmuje kl. 1–3, 4–6 i 7–8: przyrodę, biologię, chemię, fizykę i geografię.'
}, {
  label: 'Tom 1 · Strona 11',
  title: 'Lekcja 1 · Pięć zmysłów · 45 min',
  text: 'Cel, pytanie kluczowe, MATERIAŁY, INSTRUKCJA DLA NAUCZYCIELA minuta po minucie i doświadczenie „Tajemnicze woreczki”.'
}, {
  label: 'Tom 1 · Strona 12',
  title: 'Karta pracy na 3 poziomach · Pięć zmysłów',
  text: 'Ten sam temat w trzech wersjach: ★ wsparcie, ★★ standard, ★★★ wyzwanie – dla całej klasy.'
}, {
  label: 'Tom 1 · Strona 29',
  title: 'Lekcja 10 · Kwasy i zasady – wskaźnik z czerwonej kapusty',
  text: 'Doświadczenie krok po kroku z ramką BEZPIECZEŃSTWO – bez pracowni chemicznej, z produktów z domu.'
}, {
  label: 'Tom 1 · Strona 37',
  title: 'Klucz odpowiedzi do kart pracy',
  text: 'Odpowiedzi do wszystkich 12 lekcji na każdym poziomie – egzemplarz nauczyciela.'
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = pageLabels.slice(0, PAGE_SAMPLES.length).map((page, index) => ({
  src: PAGE_SAMPLES[index],
  ...page
}));
export const pageTypes = ['Gotowe lekcje', 'Karty pracy na 3 poziomach', 'Doświadczenia z BHP', 'Klucz odpowiedzi'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Przyrodniczy start w 5 minut – 180 rozgrzewek na początek lekcji',
  text: 'Krótkie rozgrzewki na pierwsze minuty lekcji: zagadki, prawda/fałsz i pytania „co się stanie, gdy…?”.',
  items: ['180 rozgrzewek', 'Na cały rok szkolny', 'Zagadki, prawda/fałsz, „co się stanie, gdy…?”', 'Z odpowiedziami'],
  benefit: 'Klasa skupiona od pierwszej minuty.'
}, {
  title: 'Baza kartkówek z przyrody – 60 kartkówek z kluczem odpowiedzi',
  text: 'Krótkie, gotowe kartkówki do szybkiego sprawdzenia wiedzy po każdym temacie.',
  items: ['60 gotowych kartkówek', 'Dla klas 4–8', 'Klucz odpowiedzi', 'Gotowe do druku'],
  benefit: 'Szybka kontrola wiedzy bez pisania od zera.'
}, {
  title: 'Zastępstwa bez stresu – 12 gotowych lekcji przyrody dla zastępcy',
  text: 'Dwanaście lekcji, które zastępca poprowadzi bez przygotowania – nawet jeśli nie uczy przyrody.',
  items: ['12 gotowych lekcji', 'Jasna instrukcja dla zastępcy', 'Gotowe do druku', 'Bez przygotowania'],
  benefit: 'Twoja nieobecność nie oznacza straconej lekcji.'
}, {
  title: 'Komunikacja z rodzicami – 50 gotowych wiadomości do dziennika elektronicznego',
  text: 'Gotowe wiadomości do Librusa lub innego dziennika: przybory, zasady oceniania, doświadczenia, wycieczki i zajęcia terenowe.',
  items: ['50 gotowych wiadomości', 'Do skopiowania i wklejenia', 'Początek roku, oceny, wycieczki', 'Uprzejmy, profesjonalny ton'],
  benefit: 'Wiadomość do rodziców w kilka sekund.'
}, {
  title: 'Przyroda w terenie – 30 gotowych zajęć na zewnątrz',
  text: 'Gotowe zajęcia na boisku, w parku i w najbliższej okolicy szkoły – z kartami obserwacji dla uczniów.',
  items: ['30 gotowych zajęć', 'Karty obserwacji', 'Krok po kroku dla nauczyciela', 'Na różne pory roku'],
  benefit: 'Zajęcia terenowe bez długich przygotowań.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Pobierz pakiet',
  text: 'Zaraz po zakupie pobierasz 6 tomów PDF i 5 bonusów, a potem otwierasz tom dla swojej klasy.'
}, {
  title: 'Wybierz klasę i lekcję',
  text: 'Sprawdź w planie rocznym, który dział przypada na ten tydzień.'
}, {
  title: 'Przygotuj się w 5 minut',
  text: 'Przeczytaj INSTRUKCJĘ DLA NAUCZYCIELA, przygotuj MATERIAŁY i wydrukuj kartę pracy na właściwym poziomie.'
}, {
  title: 'Wejdź do klasy',
  text: 'Prowadź lekcję minuta po minucie – z doświadczeniem, kartą pracy i kluczem odpowiedzi pod ręką.'
}];
export const audienceItems = ['Uczysz przyrody w klasach 4–6', 'Uczysz biologii, chemii, fizyki lub geografii w kl. 7–8', 'Prowadzisz edukację przyrodniczą w kl. 1–3', 'Zaczynasz nową przyrodę w klasie 4', 'Masz w klasie uczniów na bardzo różnych poziomach', 'Chcesz robić doświadczenia – nawet bez pracowni', 'Potrzebujesz gotowych sprawdzianów i kryteriów oceniania', 'Chcesz odzyskać wieczory i weekendy'];
export const timeValueTasks = ['Szukanie materiałów i kart pracy w internecie', 'Pisanie konspektów lekcji', 'Przygotowanie doświadczeń i zasad BHP', 'Dostosowanie kart pracy do różnych poziomów', 'Układanie sprawdzianów, kluczy i ocenianie'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 tomów PDF – 240 stron',
  detail: 'Plan roczny i 12 lekcji, przyroda kl. 4–6 (+ kl. 1–3), biologia, chemia i fizyka, geografia z zajęciami terenowymi oraz ocenianie'
}, {
  label: '5 bonusów bez dopłaty',
  detail: 'Przyrodniczy start w 5 minut, Baza kartkówek z przyrody, Zastępstwa bez stresu, Komunikacja z rodzicami i Przyroda w terenie'
}, {
  label: 'Dla klas 1–8',
  detail: 'Gotowe lekcje, karty pracy na 3 poziomach, ponad 25 doświadczeń i sprawdziany A/B z kluczem'
}];
export const offerIncludes = ['Natychmiastowy dostęp', 'Pliki PDF do pobrania', 'Gotowe do druku', 'Bez wersji papierowej'];
export const PRICE = '99 zł';
export const PRICE_BEFORE = '349 zł';

// Only add authentic reviews of PRZYRODA GOTOWA. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Jak otrzymam materiały?',
  a: 'PRZYRODA GOTOWA™ to produkt cyfrowy. Zaraz po zakupie otrzymasz dostęp do plików PDF, które możesz pobrać i zapisać. Otworzysz je na komputerze, tablecie lub telefonie i wydrukujesz tylko te strony, których potrzebujesz.'
}, {
  q: 'Czy materiały pasują do nowej przyrody w klasie 4?',
  a: 'Tak. Od 1 września 2026 r. przyroda w nowej formule wróciła do klasy 4 (w kolejnych latach obejmie klasy 5–6). Plan roczny i lekcje są ułożone pod nową przyrodę w kl. 4–6, z naciskiem na doświadczenia, obserwacje i zajęcia terenowe. PRZYRODA GOTOWA™ to niezależnie opracowany materiał dydaktyczny – nie jest publikacją MEN.'
}, {
  q: 'Dla których klas jest pakiet?',
  a: 'Dla klas 1–8. Tom 2 obejmuje przyrodę w kl. 4–6 i edukację przyrodniczą w kl. 1–3, tomy 3–5 to biologia, chemia, fizyka i geografia w kl. 7–8. Tom 1 (plan roczny i 12 lekcji) i Tom 6 (ocenianie) zawierają materiały dla wielu klas. Wybierasz to, czego potrzebuje Twoja klasa.'
}, {
  q: 'Czy naprawdę przygotuję lekcję w 5 minut?',
  a: 'PRZYRODA GOTOWA™ powstała po to, żeby skrócić przygotowanie do minimum. Otwierasz tom, wybierasz lekcję, czytasz INSTRUKCJĘ DLA NAUCZYCIELA i drukujesz kartę pracy – zwykle w około 5 minut. Przy doświadczeniach dochodzi przygotowanie materiałów z listy.'
}, {
  q: 'Czy mogę robić doświadczenia bez pracowni?',
  a: 'Tak, wiele z nich. Doświadczenia są opisane krok po kroku, z listą materiałów i ramką BEZPIECZEŃSTWO. Część korzysta z prostych materiałów, takich jak ocet, soda oczyszczona czy wywar z czerwonej kapusty. Zawsze przestrzegaj zasad BHP obowiązujących w Twojej szkole.'
}, {
  q: 'Co z uczniami na różnych poziomach i z opinią PPP?',
  a: 'Karty pracy są przygotowane na 3 poziomach: ★ wsparcie, ★★ standard i ★★★ wyzwanie. Cała klasa pracuje nad tym samym tematem, a Ty dobierasz poziom do ucznia – także do ucznia z opinią poradni psychologiczno-pedagogicznej.'
}, {
  q: 'Czy jest materiał do oceniania?',
  a: 'Tak. Tom 6 zawiera wymagania na oceny 1–6, ocenianie kształtujące, kartkówki, sprawdziany A/B z kluczem odpowiedzi (przyroda, biologia, chemia, fizyka, geografia), samoocenę ucznia i 100 gotowych komentarzy do ocen. W Tomie 1 są też sprawdziany dla kl. 4–6 i 7–8 z kluczem.'
}, {
  q: 'Czy muszę wszystko drukować?',
  a: 'Nie. To Ty decydujesz, które strony drukujesz. Karty pracy możesz też wyświetlić na tablicy interaktywnej lub rzutniku.'
}, {
  q: 'Czy 5 bonusów jest w cenie?',
  a: `Tak. Wszystkie pięć bonusów otrzymujesz bez dopłaty – razem z 6 tomami za ${PRICE}.`
}, {
  q: 'Czy to subskrypcja?',
  a: 'Nie. Płacisz jeden raz i zachowujesz dostęp do materiałów. Żadnych opłat cyklicznych.'
}, {
  q: 'Czy mogę korzystać na telefonie lub tablecie?',
  a: 'Tak. Pliki PDF otworzysz na komputerze, tablecie i telefonie. Do prowadzenia lekcji polecamy tablet, komputer z rzutnikiem albo wydruk.'
}, {
  q: 'Czy jest gwarancja?',
  a: 'Tak. PRZYRODA GOTOWA™ ma 7-dniową gwarancję satysfakcji. Jeśli materiały nie spełnią Twoich oczekiwań, napisz do nas w ciągu 7 dni od zakupu, a zwrócimy pieniądze zgodnie z warunkami gwarancji.'
}];
export const footerLinks = ['Regulamin', 'Polityka prywatności', 'Zwroty', 'Kontakt'];
